import { IonNav } from '@ionic/react';
import { createContext, createElement, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Tabs } from '../exports/Enums';
import MainLayout from '../layouts/MainLayout';
import collect from 'collect.js';
import { BrowserRouter } from 'react-router-dom';
import Cart from '../pages/cart';
import ModalRouter from './ModalRouter';
import ModalRoute from '../components/modals/ModalRoute';
import OrderDetails from '../pages/order-details';
import Checkout from '../pages/checkout';
import Rewards from '../pages/rewards';
import UploadGoogleReview from '../pages/upload-google-review';
import GlobalModalsStack from '../../../modals/GlobalModalsStack';

interface IStack {
  path: Routes;
  component: React.ComponentType<any>;
}

export interface IRouterContext {
  navigation: HTMLIonNavElement;
  tab: Tabs;
  navigate: (path: Routes, payload?: any) => void;
  reset: () => void;
  goBack: () => void;
  setTab: (tab: Tabs) => void;
}

export const RouterContext = createContext<IRouterContext | null>(null);

const Router = (): JSX.Element => {
  const navRef = useRef<HTMLIonNavElement>(null);
  const [tab, setTab] = useState<Tabs>(Tabs.Home);

  const stack: IStack[] = [
    {
      path: Routes.Main,
      component: MainLayout,
    },
    {
      path: Routes.Cart,
      component: Cart,
    },
    {
      path: Routes.OrderDetails,
      component: OrderDetails,
    },
    {
      path: Routes.Checkout,
      component: Checkout,
    },
    {
      path: Routes.Rewards,
      component: Rewards,
    },
    {
      path: Routes.UploadGoogleReview,
      component: UploadGoogleReview,
    },
  ];

  const findStack = (path: Routes): IStack => {
    return collect(stack).firstWhere('path', path);
  };

  const navigate = (path: Routes, payload?: any): void => {
    if (!navRef.current) return;

    const { component } = findStack(path);

    navRef.current.push(() => createElement(component, payload ?? {}));
  };

  const goBack = (): void => {
    if (!navRef.current) return;

    navRef.current.pop();
  };

  const reset = (): void => {
    if (!navRef.current) return;

    navRef.current.popToRoot();
  };

  const context = useMemo(
    () => ({
      navigation: navRef.current,
      tab,
      navigate,
      reset,
      goBack,
      setTab,
    }),
    [navRef.current, tab],
  );

  useEffect(() => {
    navRef.current.setRoot(() => <MainLayout />);
  }, []);

  return (
    <BrowserRouter>
      <RouterContext.Provider value={context}>
        <ModalRouter>
          <IonNav ref={navRef} />
          <ModalRoute />
        </ModalRouter>
        <GlobalModalsStack />
      </RouterContext.Provider>
    </BrowserRouter>
  );
};

export default Router;
